import { resolveMuzzleVelocity } from "./mv-temperature.js";

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

const ICAO_REFERENCE_TEMPERATURE_FAHRENHEIT = 59;

function optionalSensitivity(value) {
  return value === undefined || value === null || String(value).trim() === "" ? 0 : value;
}

/**
 * Resolve the browser-only MV/temperature model to the historical `mv` engine
 * parameter. Series calculations retain the model for their per-case adapter.
 */
export function applyMvTemperatureModel(calculator, requestValues) {
  if (!isRecord(requestValues)) {
    throw new TypeError("Calculator request values must be an object.");
  }

  const resolved = { ...requestValues };
  const mode = calculator?.mvTemperatureMode;
  if (mode === undefined || mode === "series") return resolved;
  if (!Object.prototype.hasOwnProperty.call(resolved, "mvtb")) {
    throw new Error("The muzzle-velocity temperature table is missing.");
  }

  let temperatureFahrenheit;
  let sensitivityFpsPerFahrenheit;
  if (mode === "shot") {
    temperatureFahrenheit = resolved.tp;
    sensitivityFpsPerFahrenheit = optionalSensitivity(resolved.mvs);
  } else if (mode === "icao") {
    temperatureFahrenheit = ICAO_REFERENCE_TEMPERATURE_FAHRENHEIT;
    sensitivityFpsPerFahrenheit = 0;
  } else {
    throw new TypeError(`Unsupported muzzle-velocity temperature mode “${mode}”.`);
  }

  resolved.mv = String(resolveMuzzleVelocity(
    resolved.mvtb,
    temperatureFahrenheit,
    sensitivityFpsPerFahrenheit,
  ));
  // Geometry-estimated BC is calibrated once at ICAO temperature. Keep that
  // reference fixed while the shot's launch velocity changes with temperature.
  resolved.bcrv = String(resolveMuzzleVelocity(
    resolved.mvtb,
    ICAO_REFERENCE_TEMPERATURE_FAHRENHEIT,
    0,
  ));
  delete resolved.mvtb;
  delete resolved.mvs;
  return resolved;
}

/**
 * Purely merge a calculator request with its declared Loaded Gun subset.
 * Loaded Gun values always win over stale calculator-owned copies.
 */
export function mergeLoadedGunValues(calculator, requestValues, gunState) {
  if (!isRecord(requestValues)) {
    throw new TypeError("Calculator request values must be an object.");
  }
  if (!calculator?.requiresGun) return { ...requestValues };
  if (!gunState?.configured || !isRecord(gunState.draft)) {
    throw new Error("Create or load a gun before calculating.");
  }
  if (!Array.isArray(calculator.gunParameters)) {
    throw new TypeError("A Loaded Gun calculator must declare its gun parameters.");
  }

  const merged = { ...requestValues };
  for (const name of calculator.gunParameters) {
    if (name === "sdc") {
      merged.sdc = "on";
      continue;
    }
    if (!Object.prototype.hasOwnProperty.call(gunState.draft, name)) {
      throw new Error(`The loaded gun is missing required field “${name}”.`);
    }
    merged[name] = String(gunState.draft[name]);
  }
  if (calculator.mvTemperatureMode === "series") delete merged.mv;
  return applyMvTemperatureModel(calculator, merged);
}

/** Validate cross-form constraints that cannot live on either form alone. */
export function validateLoadedGunCompatibility(calculator, requestValues) {
  if (calculator?.slug !== "point-blank-range") return;
  const sightHeight = Number(requestValues?.sh);
  const vitalZoneRadius = Number(requestValues?.vz);
  if (Number.isFinite(sightHeight) && Number.isFinite(vitalZoneRadius)
    && sightHeight > vitalZoneRadius) {
    throw new RangeError(
      "The loaded gun's sight height cannot exceed the point-blank vital-zone radius.",
    );
  }
}
